// components/layout/BackstoryLayout.tsx
import React, { JSX, ReactElement, useEffect, useState } from 'react';
import { Outlet, useLocation, Routes, Route, matchPath } from 'react-router-dom';
import { Box, Container, Paper, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { darken, useTheme } from '@mui/material/styles';
import { Header } from 'components/layout/Header';
import { Scrollable } from 'components/Scrollable';
import { Footer } from 'components/layout/Footer';
import { LoadingComponent } from 'components/LoadingComponent';
import { useAuth } from 'hooks/AuthContext';
import { getMainNavigationItems, getAllRoutes } from 'config/navigationConfig';
import { NavigationItem } from 'types/navigation';
import { ConversationHandle } from 'components/Conversation';

// Legacy type for backward compatibility
export type NavigationLinkType = {
  label: JSX.Element | string;
  path: string;
  icon?: JSX.Element | string;
};

interface BackstoryPageContainerProps {
  children?: React.ReactNode;
  variant?: 'normal' | 'fullWidth';
}

const BackstoryPageContainer = (props: BackstoryPageContainerProps): JSX.Element => {
  const { children, variant = 'normal' } = props;

  return (
    <Container
      className="BackstoryPageContainer"
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1,
        p: '0 !important',
        m: '0 auto !important',
        // minWidth: variant === 'normal' ? '1024px' : '100%',
        maxWidth: variant === 'normal' ? '1024px' : '100% !important',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          p: 0,
          m: 0,
          flexGrow: 1,
          minWidth: 0,
          minHeight: 'min-content',
        }}
      >
        <Paper
          elevation={2}
          sx={{
            display: 'flex',
            flexGrow: 1,
            m: 0,
            p: 0,
            minWidth: 0,
            minHeight: 'min-content',
            backgroundColor: 'background.paper',
            borderRadius: 0.5,
            maxWidth: '100%',
            flexDirection: 'column',
          }}
        >
          {children}
        </Paper>
      </Box>
    </Container>
  );
};

interface BackstoryLayoutProps {
  page: string;
  chatRef: React.Ref<ConversationHandle>;
}

const BackstoryLayout: React.FC<BackstoryLayoutProps> = (props: BackstoryLayoutProps) => {
  const { page, chatRef } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { guest, user } = useAuth();
  const [navigationItems, setNavigationItems] = useState<NavigationItem[]>([]);
  const userType = user?.userType || null;
  const isAdmin = user?.isAdmin ? true : false;
  const routes = getAllRoutes(userType, isAdmin);

  useEffect(() => {
    const userType = user?.userType || null;
    setNavigationItems(getMainNavigationItems(userType, user?.isAdmin ? true : false));
  }, [user]);

  // Generate dynamic routes from navigation config
  const generateRoutes = (): React.ReactNode | null => {
    if (!guest && !user) return null;

    return routes
      .map((route, index) => {
        if (!route.path || !route.component) return null;

        // Clone the component and pass necessary props if it's a page component
        const componentWithProps = React.cloneElement(route.component as ReactElement, {
          ...(route.id === 'chat' && { ref: chatRef }),
          ...(route.component.props || {}),
        });

        return (
          <Route key={`${route.id}-${index}`} path={route.path} element={componentWithProps} />
        );
      })
      .filter(Boolean);
  };

  const findMatchingRoute = (
    currentPath: string,
    routes: NavigationItem[]
  ): null | NavigationItem => {
    for (const route of routes) {
      if (!route.path) continue;

      // Try to match the route path with the current path
      const match = matchPath(
        {
          path: route.path,
          end: true,
          caseSensitive: false,
        },
        currentPath
      );

      if (match) {
        return route;
      }
    }
    return null;
  };

  // Extract 'variant' from the current route
  const currentRouteConfig = findMatchingRoute(location.pathname, routes);
  const variant = currentRouteConfig?.variant || 'normal';

  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: '100%',
        minHeight: '100%',
        flexDirection: 'column',
      }}
    >
      <Header currentPath={page} navigate={navigate} navigationItems={navigationItems} />
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          maxHeight: '100%',
          minHeight: '100%',
          flex: 1,
          m: 0,
          p: 0,
          flexDirection: 'column',
          backgroundColor: '#D3CDBF' /* Warm Gray */,
        }}
      >
        <Scrollable
          className="BackstoryPageScrollable"
          sx={{
            m: 0,
            p: 0,
            mt: '72px' /* Needs to be kept in sync with the height of Header */,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: theme => darken(theme.palette.background.default, 0.4),
            height: '100%',
            maxHeight: '100%',
            minHeight: '100%',
            minWidth: isMobile ? 0 : 'min-content',
          }}
        >
          <BackstoryPageContainer variant={variant}>
            {!guest && !user && (
              <Box>
                <LoadingComponent
                  loadingText="Creating session..."
                  loaderType="linear"
                  withFade={true}
                  fadeDuration={1200}
                />
              </Box>
            )}
            {(guest || user) && (
              <>
                <Outlet />
                <Routes>{generateRoutes()}</Routes>
              </>
            )}
            {location.pathname === '/' && <Footer />}
          </BackstoryPageContainer>
        </Scrollable>
      </Box>
    </Box>
  );
};

export { BackstoryLayout };
